/**
 * Pure geometry helpers for positioning the docked canvas inside
 * react-zoom-pan-pinch's <TransformWrapper/>. Kept framework/redux-free so both
 * DockedVisualizerWrapper's per-frame dock-follow ("cover" fit) and
 * zoomToFeatureGroup's discrete feature-group zoom share exactly one definition
 * of "how a focal point maps to positionX/positionY" instead of two formulas
 * that could quietly drift apart.
 *
 * rzpp's content transform is `translate(positionX, positionY) scale(scale)`
 * with a top-left transform-origin: a content-local point (px, py) ends up on
 * screen at (positionX + px*scale, positionY + py*scale). So to center a
 * focal point in a `viewportSize`-wide/tall wrapper:
 *   positionX = viewportWidth / 2 - focalX * scale  (same reasoning for Y)
 */

export interface Size {
  width: number;
  height: number;
}

export interface Transform {
  positionX: number;
  positionY: number;
  scale: number;
}

/**
 * Clamps a desired axis offset so the (already-scaled) content never reveals
 * empty space beyond its own edge, and centers the axis outright when the
 * scaled content is smaller than the viewport (nothing to clamp against).
 */
export function clampAxisOffset(desiredOffset: number, viewportSize: number, scaledContentSize: number): number {
  if (scaledContentSize <= viewportSize) {
    return (viewportSize - scaledContentSize) / 2;
  }
  return Math.min(0, Math.max(desiredOffset, viewportSize - scaledContentSize));
}

/**
 * Cover-fit + center transform — the "resting" fit used while the dock box is
 * animating/resizing (DockedVisualizerWrapper's syncToViewport RAF).
 */
export function computeCoverTransform({
  contentWidth,
  contentHeight,
  viewportWidth,
  viewportHeight,
  scale,
}: {
  contentWidth: number;
  contentHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  scale: number;
}): Transform {
  const scaledWidth = contentWidth * scale;
  const scaledHeight = contentHeight * scale;
  const positionX = clampAxisOffset(viewportWidth / 2 - (contentWidth / 2) * scale, viewportWidth, scaledWidth);
  const positionY = clampAxisOffset(viewportHeight / 2 - (contentHeight / 2) * scale, viewportHeight, scaledHeight);
  return { positionX, positionY, scale };
}

/**
 * Centers an arbitrary content-local focal point (e.g. a feature group's
 * bounding-box center) in the viewport at the given scale, clamped to the
 * content's own edges so zooming to a corner feature never reveals empty
 * space beyond the canvas.
 */
export function computeFocalTransform({
  contentWidth,
  contentHeight,
  viewportWidth,
  viewportHeight,
  scale,
  focalX,
  focalY,
  centerOffsetX = 0,
  centerOffsetY = 0,
}: {
  contentWidth: number;
  contentHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  scale: number;
  focalX: number;
  focalY: number;
  /**
   * Shifts the point the focal coordinate is centered ON, in viewport px, without changing the
   * viewport itself. Use it when part of the viewport is covered by an overlay (the fullscreen
   * mobile menu covers the right ~50%), so "centered" should mean centered in the part the user
   * can actually see: pass -overlayWidth / 2 to center in the free area to the LEFT of a
   * right-hand panel. Clamping still happens against the FULL viewport, so the content keeps
   * covering the whole wrapper and no empty space is ever revealed behind the overlay.
   * See getOverlayCenterOffset below.
   */
  centerOffsetX?: number;
  centerOffsetY?: number;
}): Transform {
  const scaledWidth = contentWidth * scale;
  const scaledHeight = contentHeight * scale;
  const positionX = clampAxisOffset(
    viewportWidth / 2 + centerOffsetX - focalX * scale,
    viewportWidth,
    scaledWidth
  );
  const positionY = clampAxisOffset(
    viewportHeight / 2 + centerOffsetY - focalY * scale,
    viewportHeight,
    scaledHeight
  );
  return { positionX, positionY, scale };
}

/**
 * Converts a 0-100 percent along one axis's own pannable range into the content-local focal
 * coordinate that computeFocalTransform expects — the percent-based counterpart to handing it a
 * raw focalX/focalY. 0 is this axis's start edge (as far left/up as computeFocalTransform's own
 * clamp ever allows the content to go), 50 is dead center, 100 is the end edge (as far right/down
 * as the clamp allows) — see offsetX/offsetY and IDLE_PAN_PERCENT_BY_VISUALIZER_TYPE in
 * featureGroupZoomDefaults.js, both of which are expressed in this same unit.
 *
 * This is deliberately NOT "percent of the canvas's own size offset from center" (that was the
 * old unit, and it could still saturate computeFocalTransform's clamp before reaching its nominal
 * value whenever the viewport's aspect ratio drifted from the canvas's own aspect — see git
 * history on IDLE_PAN_PERCENT_BY_VISUALIZER_TYPE for a real example). Because this percent is
 * resolved against the ACTUAL pan range for the current viewport/scale, 0 and 100 always land
 * exactly on the real edges — on any screen or dock-box size — and can never saturate.
 */
export function percentToFocalCoord(percent: number, contentSize: number, viewportSize: number, scale: number): number {
  const scaledContentSize = contentSize * scale;
  if (scaledContentSize <= viewportSize) {
    // Nothing to pan on this axis — content already fits inside the viewport, so every percent
    // value lands on the same dead-center spot (matches clampAxisOffset's own fallback).
    return contentSize / 2;
  }
  const clampedPercent = Math.min(100, Math.max(0, percent));
  const minFocal = viewportSize / (2 * scale); // percent 0 (start edge)
  const maxFocal = contentSize - viewportSize / (2 * scale); // percent 100 (end edge)
  return minFocal + (clampedPercent / 100) * (maxFocal - minFocal);
}

/**
 * The wrapper element's live, on-screen size (in CSS px) — the "viewport" that
 * content gets fit/centered into. Returns null before the TransformWrapper has
 * mounted/measured, so callers can bail out gracefully instead of falling back
 * to window dimensions (that fallback is only appropriate for the fullscreen
 * cover-fit in DockedVisualizerWrapper, not for feature-group zooming).
 */
export function getWrapperViewportRect(rzppRef: any): Size | null {
  const instance = rzppRef && rzppRef.instance;
  const wrapperEl =
    (instance && instance.wrapperComponent) || (instance && instance.wrapperRef && instance.wrapperRef.current) || null;
  if (!wrapperEl) {
    return null;
  }
  const rect = wrapperEl.getBoundingClientRect();
  return { width: rect.width, height: rect.height };
}

/** The TransformWrapper's live wrapper element, or null before it has mounted. */
export function getWrapperElement(rzppRef: any): HTMLElement | null {
  const instance = rzppRef && rzppRef.instance;
  return (
    (instance && instance.wrapperComponent) || (instance && instance.wrapperRef && instance.wrapperRef.current) || null
  );
}

export interface CenterOffset {
  x: number;
  y: number;
}

const NO_CENTER_OFFSET: CenterOffset = { x: 0, y: 0 };

/**
 * How far to shift "the middle of the screen" so it lands in the middle of the part of the
 * viewport an edge-anchored overlay panel is NOT covering — the value to hand
 * computeFocalTransform as centerOffsetX/centerOffsetY.
 *
 * The fullscreen mobile visualizer has the navigation menu docked to the right edge
 * (scss/components/mobilemenu.scss: `.menu { position: fixed; right: 0; width: 50%; height: 100% }`,
 * 60% under 580px), overlaying the canvas rather than shrinking it the way the embedded
 * /materials dock box does. Without this correction, zooming to a feature group would center it
 * under the menu — technically framed, actually invisible.
 *
 * Measured from offsetWidth/offsetHeight (the LAYOUT box) rather than getBoundingClientRect(),
 * because that menu slides in via `transform: translateX(100%)`: its bounding rect is wrong for
 * the whole ~300ms of the open animation, while its layout box is already final on the first
 * frame. The anchored edge comes from the computed `left`/`top` being auto (or beyond the
 * midpoint), which is how such a panel is positioned.
 *
 * Returns {0, 0} — i.e. plain centering — for anything that isn't an edge-anchored panel:
 * a missing or `display: none` overlay (the menu is display:none above the mobile breakpoint),
 * or one that covers the viewport in both axes (nothing useful is left to center in).
 */
export function getOverlayCenterOffset(
  wrapperEl: HTMLElement | null,
  overlayEl: HTMLElement | null
): CenterOffset {
  if (!wrapperEl || !overlayEl || typeof window === "undefined") {
    return NO_CENTER_OFFSET;
  }
  const overlayWidth = overlayEl.offsetWidth;
  const overlayHeight = overlayEl.offsetHeight;
  if (!overlayWidth || !overlayHeight) {
    return NO_CENTER_OFFSET; // display:none / not laid out — e.g. the menu above the mobile breakpoint
  }
  const wrapperRect = wrapperEl.getBoundingClientRect();
  if (!wrapperRect.width || !wrapperRect.height) {
    return NO_CENTER_OFFSET;
  }

  // "Spans" with a little slack, so a panel with a few px of inset still counts as full-bleed.
  const spansHeight = overlayHeight >= wrapperRect.height * 0.9;
  const spansWidth = overlayWidth >= wrapperRect.width * 0.9;
  if (spansHeight === spansWidth) {
    return NO_CENTER_OFFSET; // covers everything, or is a floating box — no single free half
  }

  const style = window.getComputedStyle(overlayEl);
  if (spansHeight) {
    const leftPx = parseFloat(style.left);
    const anchoredLeft = !isNaN(leftPx) && leftPx < wrapperRect.width / 2;
    // Free area is on the opposite side of the panel; shift the centering target into it.
    return { x: anchoredLeft ? overlayWidth / 2 : -overlayWidth / 2, y: 0 };
  }
  const topPx = parseFloat(style.top);
  const anchoredTop = !isNaN(topPx) && topPx < wrapperRect.height / 2;
  return { x: 0, y: anchoredTop ? overlayHeight / 2 : -overlayHeight / 2 };
}
